import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Loader2 }                  from 'lucide-react';

import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { whatsappApi }    from '@/lib/api';
import { QK }             from '@/lib/query-client';
import { StepEmpresa }    from '@/pages/onboarding/steps/step-empresa';
import { StepWhatsapp }   from '@/pages/onboarding/steps/step-whatsapp';
import { useAppStore, selectActiveTenant }    from '@/store/app.store';

export function SetupPromptDialog() {
  const {
    companies,
    companiesLoaded,
    dismissedSetupPrompt,
    dismissSetupPrompt,
  } = useAppStore();
  const activeTenant = useAppStore(selectActiveTenant);
  const queryClient = useQueryClient();

  const hasCompanies = companiesLoaded && companies.length > 0;

  const { data: waInstances, isLoading: waLoading } = useQuery({
    queryKey: QK.whatsappInstances(),
    queryFn:  () => whatsappApi.list().then(r => r.data),
    enabled:  hasCompanies && !dismissedSetupPrompt,
    staleTime: 30_000,
  });

  if (dismissedSetupPrompt || !companiesLoaded) return null;

  const noCompany  = companies.length === 0;
  const waChecked  = !waLoading && waInstances !== undefined;
  const noWhatsapp = hasCompanies && waChecked && !waInstances.some(i => i.status === 'CONNECTED');

  // While companies exist but WA status is still loading, keep dialog open with spinner
  const showLoader = hasCompanies && !waChecked;

  const prompt: 'company' | 'whatsapp' | null =
    noCompany ? 'company' : noWhatsapp ? 'whatsapp' : null;

  if (!prompt && !showLoader) return null;

  function handleWaConnected() {
    void queryClient.invalidateQueries({ queryKey: QK.whatsappInstances() });
  }

  return (
    <Dialog
      open
      onOpenChange={(open) => { if (!open) dismissSetupPrompt(); }}
    >
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        onInteractOutside={(e) => { e.preventDefault(); }}
        onEscapeKeyDown={(e) => { e.preventDefault(); }}
      >
        <DialogTitle className="sr-only">Asistente de configuración</DialogTitle>
        {showLoader && (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="h-8 w-8 animate-spin text-[#2980b9]" />
          </div>
        )}

        {!showLoader && prompt === 'company' && (
          <>
            <DialogHeader className="mb-2">
              <DialogTitle>Registra tu primera empresa</DialogTitle>
              <DialogDescription>
                Agrega una empresa cliente para comenzar a usar Ecocont Estudio.
                Puedes hacerlo más tarde desde el panel Empresas.
              </DialogDescription>
            </DialogHeader>
            <StepEmpresa
              onNext={() => {
                // StepEmpresa already calls invalidateQueries(QK.companies()) on success.
                // companiesLoaded + companies refetch will re-evaluate this dialog.
              }}
              onBack={dismissSetupPrompt}
            />
          </>
        )}

        {!showLoader && prompt === 'whatsapp' && (
          <>
            <DialogHeader className="mb-2">
              <DialogTitle>Conecta WhatsApp</DialogTitle>
              <DialogDescription>
                Escanea el código QR para vincular WhatsApp y enviar alertas a tus clientes.
                Puedes configurarlo más tarde en Configuración → WhatsApp.
              </DialogDescription>
            </DialogHeader>
            <StepWhatsapp
              tenantName={activeTenant?.name ?? 'Tu estudio'}
              company={companies[0] ?? null}
              onNext={handleWaConnected}
              onBack={dismissSetupPrompt}
              onSkip={dismissSetupPrompt}
              compact
            />
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
